import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  User, Lock, Trash2, Loader2, ChevronLeft, 
  Check, AlertCircle, Settings as SettingsIcon, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const [username, setUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users/me/profile`);
      setProfile(response.data);
      setUsername(response.data.anonymous_username || '');
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // Calculate days until username can be changed
  const getUsernameChangeInfo = () => {
    if (!profile?.last_username_change) {
      return { canChange: true, daysRemaining: 0 };
    }
    const lastChange = parseISO(profile.last_username_change);
    const daysSince = differenceInDays(new Date(), lastChange);
    const daysRemaining = Math.max(0, 30 - daysSince);
    return { canChange: daysRemaining === 0, daysRemaining };
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || username.length < 3 || username.length > 20) {
      toast.error('Username must be 3-20 characters');
      return;
    }
    
    if (!/^[a-zA-Z0-9]+$/.test(username)) {
      toast.error('Username must be alphanumeric only');
      return;
    }

    setUsernameLoading(true);
    try {
      await axios.put(`${API}/users/me/username`, {
        anonymous_username: username
      });
      toast.success('Username updated successfully!');
      await refreshUser();
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update username');
    } finally {
      setUsernameLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.put(`${API}/users/me/password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/users/me/data`);
      toast.success('Account deleted successfully');
      await logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleteLoading(false);
      setDeleteModalOpen(false);
    }
  };

  const { canChange, daysRemaining } = getUsernameChangeInfo();
  const isClerkUser = user?.auth_provider === 'clerk' || !profile?.password_hash;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-4 sm:px-6 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)} 
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-6"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
            <SettingsIcon className="w-6 h-6 text-red-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Settings</h1>
            <p className="text-neutral-400">Manage your account settings</p>
          </div>
        </div>

        {/* Username Section */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <User className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold text-white">Username</h2>
          </div>
          
          <p className="text-neutral-400 text-sm mb-4">
            This is your public display name shown on sidequests and submissions.
            You can change it once per month.
          </p>

          {!canChange && (
            <div className="flex items-center gap-2 p-3 bg-yellow-500/10 rounded-xl mb-4">
              <Calendar className="w-4 h-4 text-yellow-500" />
              <span className="text-sm text-yellow-400">
                You can change your username again in {daysRemaining} days
              </span>
            </div>
          )}

          <form onSubmit={handleUsernameChange} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-white">Username</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="YourUsername"
                className="bg-white/5 border-white/10 text-white mt-1"
                disabled={!canChange}
                maxLength={20}
              />
              <p className="text-xs text-neutral-500 mt-1">
                3-20 alphanumeric characters only
              </p>
            </div>
            <Button 
              type="submit" 
              disabled={usernameLoading || !canChange || username === profile?.anonymous_username}
              className="bg-red-500 hover:bg-red-600"
            >
              {usernameLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Update Username
            </Button>
          </form>
        </div>

        {/* Password Section - Only for non-Clerk users */}
        {!isClerkUser && (
          <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-5 h-5 text-red-500" />
              <h2 className="text-lg font-semibold text-white">Change Password</h2>
            </div>
            
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label htmlFor="currentPassword" className="text-white">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label htmlFor="newPassword" className="text-white">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
                <p className="text-xs text-neutral-500 mt-1">Minimum 8 characters</p>
              </div>
              <div>
                <Label htmlFor="confirmPassword" className="text-white">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <Button 
                type="submit" 
                disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                className="bg-red-500 hover:bg-red-600"
              >
                {passwordLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Change Password
              </Button>
            </form>
          </div>
        )}

        {isClerkUser && (
          <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-5 h-5 text-red-500" />
              <h2 className="text-lg font-semibold text-white">Password</h2>
            </div>
            <p className="text-neutral-400">
              You signed in with Google. Password management is handled through your Google account.
            </p>
          </div>
        )}

        {/* Delete Account Section */}
        <div className="bg-[#121212] rounded-2xl border border-red-500/30 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Trash2 className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold text-white">Delete Account</h2>
          </div>
          
          <p className="text-neutral-400 text-sm mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>

          <div className="p-4 bg-red-500/10 rounded-xl mb-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-400">
                <p className="font-semibold mb-1">This will permanently delete:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Your account and profile</li>
                  <li>All sidequest submissions</li>
                  <li>All bookings and reviews</li>
                  <li>Your business (if you have one)</li>
                </ul>
              </div>
            </div>
          </div>

          <Button 
            variant="destructive"
            onClick={() => setDeleteModalOpen(true)}
            className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete My Account
          </Button>
        </div>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="bg-[#121212] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Delete Account?</DialogTitle>
              <DialogDescription className="text-neutral-400">
                This action is permanent and cannot be undone. All your data will be deleted.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Label htmlFor="deleteConfirm" className="text-white">
                Type <span className="text-red-500 font-bold">DELETE</span> to confirm
              </Label>
              <Input
                id="deleteConfirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="bg-white/5 border-white/10 text-white mt-2"
              />
            </div>
            
            <DialogFooter>
              <Button 
                variant="ghost" 
                onClick={() => setDeleteModalOpen(false)}
                className="text-neutral-400"
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={deleteLoading || deleteConfirmText !== 'DELETE'}
                className="bg-red-500 hover:bg-red-600"
              >
                {deleteLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
